import React, { useState } from 'react';
import { ZoomIn, Download, Maximize2, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTrigger } from './ui/dialog';
import { projectInfo, projectSpecs } from '../data/mock';

const SitePlanSection = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <section id="siteplan" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-emerald-600 font-semibold text-sm tracking-wider uppercase mb-4 block">
            Development Layout
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Site Plan & Specifications
          </h2>
          <p className="text-slate-600 text-lg max-w-3xl mx-auto leading-relaxed">
            Comprehensive layout showcasing building footprints, parking configurations, 
            pedestrian routes, and strategic positioning along Stadium Drive.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Site Plan Image */}
          <div className="lg:col-span-2">
            <div
              className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 group"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <img
                src={projectInfo.heroImage}
                alt="Stadium Plaza Center Site Plan"
                className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
              />
              
              {/* Overlay Controls */}
              <div className={`absolute inset-0 bg-slate-900/40 flex items-center justify-center gap-4 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg">
                      <Maximize2 size={18} className="mr-2" />
                      View Full Size
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-6xl w-full">
                    <img
                      src={projectInfo.heroImage}
                      alt="Stadium Plaza Center Site Plan - Full Size"
                      className="w-full h-auto rounded-lg"
                    />
                  </DialogContent>
                </Dialog>
              </div>

              {/* Info Badge */}
              <div className="absolute top-4 left-4 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                <Info size={16} />
                <span className="text-sm font-medium">Interactive Site Plan</span>
              </div>
            </div>
          </div>

          {/* Specifications Panel */}
          <div className="lg:col-span-1">
            <div className="bg-slate-900 rounded-2xl p-8 h-full">
              <h3 className="text-white text-xl font-bold mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <ZoomIn size={20} />
                </div>
                Project Specifications
              </h3>
              
              <div className="space-y-4">
                {projectSpecs.map((spec, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center py-4 border-b border-slate-700 last:border-0"
                  >
                    <span className="text-slate-400 text-sm">{spec.label}</span>
                    <span className="text-white font-semibold">{spec.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-emerald-600/20 rounded-xl border border-emerald-500/30">
                <p className="text-emerald-300 text-sm">
                  <strong className="text-emerald-200">Note:</strong> Building configurations
                  and suite sizes are flexible to accommodate tenant requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SitePlanSection;
